import { useQueryClient } from '@tanstack/react-query';
import { Form } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { NOT_APPLICABLE_SPACE_ID } from 'app/components/commons/Experience/PracticalInfo/SpacesSelect/SpacesSelect';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { CORE_QUERY_KEYS } from 'app/hooks/data/core/admin/experiences';
import {
  mapExperienceToCreateRequest,
  mapExperienceToUpdateRequest,
} from 'app/hooks/data/core/admin/experiences/mapping/form';
import { useCreateExperience } from 'app/hooks/data/core/admin/experiences/useCreateExperience';
import { useDeleteExperience } from 'app/hooks/data/core/admin/experiences/useDeleteExperience';
import { useUpdateExperience } from 'app/hooks/data/core/admin/experiences/useUpdateExperience';
import type { ExperienceDurationDescription } from 'app/typings/core/common/experiences/enum/experienceDurationDescription';
import { dayjs } from 'app/utils/dayjs';
import { pluralize } from 'app/utils/strings';

import type { ExperienceInitialFormType } from './useExperienceData';
import {
  resetFormDirty,
  resetValidationErrors,
  setStockValidationError,
  setValidationErrors,
  validateAllConfigurations,
} from './useValidationStore';

type UseExperienceActionsProps = {
  experienceId?: number;
  isCreatePage: boolean;
  initialValues?: Partial<ExperienceInitialFormType>;
};

export const useExperienceActions = ({
  experienceId,
  isCreatePage,
  initialValues,
}: UseExperienceActionsProps) => {
  const [form] = Form.useForm<ExperienceInitialFormType>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const formValues = Form.useWatch([], form);

  const { mode, enterViewMode, enterEditMode } =
    useContext(ViewEditModeContext);

  const { mutateAsync: createExperience } = useCreateExperience();
  const { mutateAsync: updateExperience } = useUpdateExperience();
  const { mutateAsync: deleteExperience } = useDeleteExperience();

  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isCreatePage) {
      enterEditMode();
    }
  }, [isCreatePage, enterEditMode]);

  const formatDuration = (
    duration?: number,
    durationDescription?: ExperienceDurationDescription
  ): string | null => {
    if (!duration || !durationDescription) {
      return null;
    }

    const durationMap: Record<ExperienceDurationDescription, string> = {
      MINUTE: pluralize(duration, 'min', 'mins'),
      HOUR: pluralize(duration, 'hour', 'hours'),
      HALF_DAY: 'half day',
      DAY: pluralize(duration, 'day', 'days'),
    };

    return durationMap[durationDescription];
  };

  const formatName = useCallback(
    (experience: ExperienceInitialFormType, categoryName?: string): string => {
      const parts: string[] = [];

      if (categoryName) {
        parts.push(categoryName);
      }

      if (experience.location) {
        parts.push(experience.location);
      }

      const duration = formatDuration(
        experience.duration,
        experience.durationDescription
      );

      if (duration) {
        parts.push(duration);
      }

      return parts.join(' • ');
    },
    []
  );

  const onFinish = useCallback(
    async (values: ExperienceInitialFormType) => {
      const allValues = {
        ...form.getFieldsValue(),
        ...values,
      } as ExperienceInitialFormType;

      const cleanedValues = {
        ...allValues,
        closedDaysBeforeCheckIn: values.lastMinuteRule
          ? values.closedDaysBeforeCheckIn
          : null,
        maxQuantity: values.maxAddonQuantity ? values.maxQuantity : null,
        spaceIds:
          values.spaceIds?.filter((id) => id !== NOT_APPLICABLE_SPACE_ID) || [],
        time: values.time ? dayjs(values.time).format('HH:mm') : undefined,
      };

      try {
        if (isCreatePage) {
          const createdExperience = await createExperience(
            mapExperienceToCreateRequest(cleanedValues)
          );

          const location = {
            pathname: `/activities/${createdExperience?.id}`,
          };

          history.push(location);
        } else if (experienceId) {
          await updateExperience({
            id: experienceId,
            payload: mapExperienceToUpdateRequest(cleanedValues),
          });

          queryClient.invalidateQueries([
            CORE_QUERY_KEYS.experience,
            experienceId,
          ]);

          enterViewMode();
        } else {
          return;
        }

        resetFormDirty();
      } finally {
        setIsLoading(false);
      }
    },
    [
      createExperience,
      enterViewMode,
      experienceId,
      form,
      history,
      isCreatePage,
      queryClient,
      updateExperience,
    ]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    resetValidationErrors();
    resetFormDirty();
    enterViewMode();
  }, [form, enterViewMode]);

  const handleDelete = useCallback(async () => {
    if (experienceId) {
      await deleteExperience(experienceId);
      history.push('/activities');
    }
  }, [deleteExperience, experienceId, history]);

  const handleSave = useCallback(() => {
    const { stockError, priceError } = validateAllConfigurations(formValues);

    if (!stockError.isValid || !priceError.isValid) {
      setValidationErrors({
        stockError,
        priceError,
      });

      setCanSubmit(false);

      return;
    }

    setStockValidationError({ isValid: true, errors: [] });

    form.submit();
    setIsLoading(true);
  }, [form, formValues]);

  return {
    form,
    mode,
    canSubmit,
    isLoading,
    onFinish,
    onCancel,
    handleDelete,
    handleSave,
    enterEditMode,
    enterViewMode,
    setCanSubmit,
    formatName,
  };
};
